import 'jodit-pro/es5/jodit.css';
import './rich-text.css';

import type { JoditPro } from 'jodit-pro/types/jodit-pro';
import type { ClipboardEvent } from 'react';
import type { NewMediaUploadFn } from '../../hooks/api/useMediaUpload';
import type { WebsocketAwarenessState } from '../../hooks/useWebsocket/websocket';
import type {
  ContentObject,
  Media,
} from '../../lib/flotiq-client/types/api-response';
import type { HelpErrorTextsProps } from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';

import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Jodit } from 'jodit-pro/es2015/jodit.js';
import { twMerge } from 'tailwind-merge';

import { getEnv } from '../../lib/env';
import { getContentObject, getContentType } from '../../lib/flotiq-client';
import { getMediaUrl } from '../../lib/flotiq-client/api-helpers';
import { EMPTY_FUNCTION, EMPTY_OBJECT } from '../../lib/globals';
import { getObjectTitle, getTestProps } from '../../lib/helpers';

import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import { useModals } from '../../contexts/ModalContext';

import FieldEditorsIndicator from '../FieldEditorsIndicator/FieldEditorsIndicator';
import FileButton from '../FileButton/FileButton';
import HelpErrorTextsTemplate from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import LinkObjectContentModal from '../LinkObjectContentModal/LinkObjectContentModal';
import RequiredTemplate from '../RequiredTemplate/RequiredTemplate';
import eventAttributes from './EventAttributes';
import { linkIcon, mediaIcon } from './icons';

const PARAGRAPH_DATA = {
  p: 'Normal',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  pre: 'Code',
  blockquote: 'Quote',
};

export interface RichTextProps extends HelpErrorTextsProps {
  /**
   * Field form name
   */
  name?: string;
  /**
   * Rich Text value
   */
  value?: string;
  /**
   * On change handler
   */
  onChange?: (value: string, name?: string) => void;
  /**
   * On blur handler
   */
  onBlur?: (name?: string) => void;
  /**
   * On focus handler
   */
  onFocus?: (name?: string) => void;
  /**
   * Label above the editor
   */
  label?: string;
  /**
   * If value is required
   */
  required?: boolean;
  /**
   * If the editor is disabled
   */
  disabled?: boolean;
  /**
   * If editor is readonly (non-interactive but without disabled classes)
   */
  readonly?: boolean;
  /**
   * On media upload callback
   */
  onMediaUpload?: NewMediaUploadFn;
  /**
   * Additional classes for editor
   */
  additionalClasses?: string;
  /**
   * Jodit Editor additional config
   */
  editorConfig?: object;
  /**
   * Editors data who are currently editing the field
   */
  editors?: WebsocketAwarenessState[];
  /**
   * Rich Text test id
   */
  testId?: string;
}

const RichText = ({
  name,
  value = '',
  onChange = EMPTY_FUNCTION,
  onBlur = EMPTY_FUNCTION,
  onFocus,
  label = '',
  error,
  required = false,
  disabled = false,
  readonly = false,
  helpText = '',
  onMediaUpload,
  additionalClasses = '',
  editorConfig = EMPTY_OBJECT,
  editors,
  testId = '',
}: RichTextProps) => {
  const { t, i18n } = useTranslation();
  const modalId = useId();
  const modal = useModals();
  const timeoutId = useRef<NodeJS.Timeout>(null);
  const editor = useRef<JoditPro>(null);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const titleCache = useRef<
    Record<string, Record<string, string | Promise<string | undefined>>>
  >({});
  const ctdNameCache = useRef<
    Record<string, string | Promise<string | undefined>>
  >({});
  const joditWrapperRef = useRef<HTMLDivElement>(null);
  const joditStateRef = useRef('active');

  const jwt = useToken();
  const { space } = useSelectedSpace();

  const editorReadonly = disabled || readonly;

  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const addMediaToEditor = useCallback(async () => {
    const media: Record<string, Media> = await modal({
      id: `${modalId}-add-media`,
      title: (
        <div className="flex flex-wrap justify-between gap-1">
          <div className="text-3xl">{t('Global.MediaLibrary')}</div>
          <FileButton
            onUpload={onMediaUpload}
            multiple
            additionalClasses="w-fit"
            testId={testId}
          />
        </div>
      ),
      content: (
        <LinkObjectContentModal
          relationType="_media"
          onMediaUpload={onMediaUpload}
          returnContentObject
          isMultiple={
            getEnv('VITE_TEXT_EDITORS_MULTIPLE_MEDIA_MODAL') === 'true'
          }
          {...getTestProps(testId, 'link-modal', 'testId')}
        />
      ),
      size: '2xl',
      dialogAdditionalClasses: 'h-[calc(100vh-32px)]',
    });

    if (media && Object.keys(media).length) {
      Object.values(media).forEach((newMedia) => {
        const mediaUrl = getMediaUrl(newMedia, 0, 0, false);
        if (newMedia.type === 'image') {
          const image = document.createElement('img');
          image.setAttribute('src', mediaUrl);
          editor.current?.selection?.insertHTML?.(image);
        } else if (newMedia.mimeType?.includes('video')) {
          const video = document.createElement('video');
          video.setAttribute('src', mediaUrl);
          video.setAttribute('controls', '');
          editor.current?.selection?.insertHTML?.(video);
        } else {
          const file = document.createElement('a');
          file.setAttribute('href', mediaUrl);
          const textContent = document.createTextNode(mediaUrl);
          file.appendChild(textContent);
          editor.current?.selection?.insertHTML?.(file);
        }
      });
    }
  }, [modal, modalId, t, onMediaUpload, testId]);

  const addRelationToEditor = useCallback(async () => {
    const object: Record<string, ContentObject> = await modal({
      id: `${modalId}-add-relation`,
      title: (
        <div className="text-3xl">{t('ContentForm.Relation.LinkObject')}</div>
      ),
      content: (
        <LinkObjectContentModal
          returnContentObject
          {...getTestProps(testId, 'link-relation-modal', 'testId')}
        />
      ),
      size: '2xl',
      dialogAdditionalClasses: 'h-[calc(100vh-32px)]',
    });

    if (object && Object.keys(object).length) {
      const newObject = Object.values(object)[0];
      const ctd = newObject.internal?.contentType;
      const title = getObjectTitle(newObject);

      const span = document.createElement('span');
      span.setAttribute('data-relation-object-id', newObject.id);
      span.setAttribute('data-relation-object-type', ctd);
      span.setAttribute('data-relation-object-title', title);
      span.setAttribute(
        'data-relation-url',
        `/api/v1/content/${ctd}/${newObject.id}`,
      );

      if (!titleCache.current[ctd]) titleCache.current[ctd] = {};
      if (!titleCache.current[ctd][newObject.id]) {
        titleCache.current[ctd][newObject.id] = title;
      }

      editor.current?.selection?.insertHTML?.(span);
    }
  }, [modal, modalId, t, testId]);

  const addNbspToEditor = useCallback(() => {
    editor.current?.selection?.insertHTML?.('&nbsp;');
  }, []);

  const buttons = useMemo(
    () => [
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'eraser',
      '|',
      'ul',
      'ol',
      '|',
      'align',
      '|',
      'hr',
      '|',
      'link',
      '|',
      'paragraph',
      'font',
      'fontsize',
      '|',
      'brush',
      '|',
      'table',
      '|',
      'image',
      {
        name: 'Media library',
        icon: mediaIcon,
        tooltip: t('RichText.MediaLibrary'),
        exec: async () => {
          await addMediaToEditor();
        },
      },
      {
        name: 'Relation',
        icon: linkIcon,
        tooltip: t('RichText.Relation'),
        exec: async () => {
          await addRelationToEditor();
        },
      },
      '|',
      {
        name: '&nbsp;',
        tooltip: t('RichText.Nbsp'),
        exec: () => {
          addNbspToEditor();
        },
      },
      '|',
      'source',
      'fullsize',
    ],
    [t, addMediaToEditor, addRelationToEditor, addNbspToEditor],
  );

  const envParagraphOrder = getEnv('VITE_JODIT_PARAGRAPH_ORDER');

  const paragraphOrder = useMemo(() => {
    if (envParagraphOrder) return envParagraphOrder.split(',');
    return ['p', 'h1', 'h2', 'h3', 'h4', 'pre', 'blockquote'];
  }, [envParagraphOrder]);

  const paragraphObject = useMemo(
    () =>
      paragraphOrder
        .filter(
          (paragraphName: string) =>
            paragraphName in PARAGRAPH_DATA &&
            !!PARAGRAPH_DATA[paragraphName as keyof typeof PARAGRAPH_DATA],
        )
        .reduce(
          (paragraphObject: Record<string, string>, paragraphName: string) => {
            if (!paragraphObject[paragraphName])
              paragraphObject[paragraphName] = '';
            paragraphObject[paragraphName] =
              PARAGRAPH_DATA[paragraphName as keyof typeof PARAGRAPH_DATA];
            return paragraphObject;
          },
          {} as Record<string, string>,
        ),
    [paragraphOrder],
  );

  const handleEventAttributeChange = useCallback(() => {
    if (
      !editor.current ||
      editor.current.isDestructed ||
      editor.current.getMode() === Jodit.constants.MODE_SOURCE
    )
      return;

    const items = editor.current.editor.querySelectorAll(
      eventAttributes.map((attribute) => `[${attribute}]`).join(', '),
    );

    items?.forEach((item) => {
      for (const attr of item.attributes) {
        if (!eventAttributes.includes(attr.name)) {
          continue;
        }

        item.removeAttribute(attr.name);
        item.setAttribute(`data-xss-protection-${attr.name}`, attr.value);
      }
    });
  }, []);

  const handleEventAttributeRestore = useCallback((value: string) => {
    const regex = /\s?data-xss-protection-([\w-]+)="([^"]*)"/g;
    return value?.replaceAll(regex, (_match, p1, p2) => {
      return ` ${p1}="${p2}"`;
    });
  }, []);

  const controls = useMemo(
    () => ({
      paragraph: {
        list: Jodit.atom(paragraphObject),
      },
      font: {
        list: Jodit.atom({
          '': 'Default',
          'Arial,Helvetica,sans-serif': 'Arial',
          'Comic Sans MS, cursive': 'Comic Sans MS',
          'Courier New,Courier,monospace': 'Courier New',
          'Georgia,serif': 'Georgia',
          'Lucida Sans Unicode,Lucida Grande,sans-serif': 'Lucida Sans Unicode',
          'Tahoma,Geneva,sans-serif': 'Tahoma',
          'Times New Roman,Times,serif': 'Times New Roman',
          'Trebuchet MS,Helvetica,sans-seri': 'Trebuchet MS',
          'Verdana,Geneva,sans-serif': 'Verdana',
        }),
      },
    }),
    [paragraphObject],
  );

  const onChangeLocal = useCallback(
    (editorValue: string) => {
      if (timeoutId.current) clearTimeout(timeoutId.current);

      const newValue = handleEventAttributeRestore(editorValue);
      if (newValue === valueRef.current) return;

      timeoutId.current = setTimeout(
        () => onChange(handleEventAttributeRestore(editorValue), name),
        250,
      );
    },
    [handleEventAttributeRestore, name, onChange],
  );

  const injectRelationTitles = useCallback(() => {
    if (
      !editor.current ||
      editor.current.isDestructed ||
      editor.current.getMode() === Jodit.constants.MODE_SOURCE
    )
      return;

    editor.current.editor
      .querySelectorAll(
        '[data-relation-object-type]:not([data-relation-object-title])',
      )
      ?.forEach((element) => {
        element.setAttribute('contenteditable', 'false');

        const type = element.getAttribute('data-relation-object-type');
        const id = element.getAttribute('data-relation-object-id');

        if (!type || !id) return;

        if (!titleCache.current[type]) titleCache.current[type] = {};
        if (!titleCache.current[type][id]) {
          titleCache.current[type][id] = getContentObject(jwt, space, {
            contentTypeName: type,
            id,
          }).then((res) => {
            if (res.ok) return getObjectTitle(res.body);
          });
        }
        if (typeof titleCache.current[type][id] !== 'string')
          titleCache.current[type][id].then((title: any) =>
            element.setAttribute('data-relation-object-title', title || id),
          );

        if (!ctdNameCache.current[type]) {
          ctdNameCache.current[type] = getContentType(jwt, space, {
            contentTypeName: type,
          }).then((res) => {
            if (res.ok) return res.body.label || res.body.name;
          });
        }
        if (typeof ctdNameCache.current[type] !== 'string')
          ctdNameCache.current[type].then((title) =>
            element.setAttribute('data-relation-type-label', title || type),
          );
      });
  }, [jwt, space]);

  useEffect(() => {
    if (!editor?.current?.isReady) return;

    if (
      editor.current.value !== value &&
      !editor.current.selection?.isFocused()
    ) {
      editor.current.setEditorValue(value || '');
      handleEventAttributeChange();
    }
  }, [editorReadonly, handleEventAttributeChange, value]);

  useEffect(() => {
    if (
      editor.current &&
      editor.current?.currentPlace?.options?.readonly !== editorReadonly
    ) {
      editor.current.setReadOnly(editorReadonly);
    }
  }, [editorReadonly]);

  useEffect(() => {
    if (!editor.current && textarea.current) {
      Jodit.lang['pl']['Paste & clean HTML styles'] =
        'Wklej i wyczyść style HTML';
      editor.current = Jodit.make(textarea.current, {
        license: getEnv('VITE_JODIT_LICENSE_KEY'),
        toolbarSticky: true,
        beautifyHTML: false,
        toolbarStickyOffset: 63,
        placeholder: '',
        allowResizeY: true,
        allowResizeX: true,
        language: i18n.language,
        height: 400,
        width: '100%',
        readonly: editorReadonly,
        buttons: buttons,
        buttonsMD: buttons,
        buttonsSM: buttons,
        buttonsXS: buttons,
        tabIndex: 0,
        controls,
        colorPickerDefaultTab: 'color',
        pasteHTMLActionList: Jodit.atom([
          { value: Jodit.constants.INSERT_AS_HTML, text: 'Keep' },
          { value: Jodit.constants.INSERT_AS_TEXT, text: 'Insert as Text' },
          {
            value: Jodit.constants.INSERT_CLEAR_HTML,
            text: 'Paste & clean HTML styles',
          },
          { value: Jodit.constants.INSERT_ONLY_TEXT, text: 'Insert only Text' },
        ]),
        cleanHTML: {
          denyTags: { script: false },
          removeEmptyElements: false,
          fillEmptyParagraph: false,
        },
        pasteFromWord: {
          enable: true,
        },
        value: value || '',
        globalFullSize: false,
        events: {
          afterResize: () => {
            const fullSize = editor.current?.isFullSize;
            if (fullSize) document.body.classList.add('overflow-hidden');
            else document.body.classList.remove('overflow-hidden');
          },
          beforePaste: (event: ClipboardEvent<HTMLTextAreaElement>) => {
            const clipboardData = event.clipboardData;
            if (!clipboardData || !onMediaUpload) return;

            // Check for direct image files
            const imageItems = Array.from(clipboardData.items).filter((item) =>
              item.type.startsWith('image/'),
            );

            if (imageItems.length > 0) {
              event.preventDefault();

              const filesToUpload = imageItems
                .map((item) => item.getAsFile())
                .filter((file) => !!file) as File[];

              onMediaUpload(filesToUpload)
                .then((uploadedFiles) => {
                  if (!uploadedFiles) return;
                  uploadedFiles.forEach(({ media, state }) => {
                    if (state === 'uploaded') {
                      const mediaUrl = getMediaUrl(media);
                      const image = document.createElement('img');
                      image.setAttribute('src', mediaUrl);
                      if (editor.current?.selection)
                        editor.current.selection.insertHTML(image);
                    }
                  });
                })
                .catch((error) => {
                  console.error('Failed to upload pasted image:', error);
                });

              return false;
            }
          },
          afterGetValueFromEditor: (data: { value: string }) => {
            const titleCleanupRegex = /\s?data-relation-object-title="[^"]+"/g;
            const ctdCleanupRegex = /\s?data-relation-type-label="[^"]+"/g;
            const editableCleanupRegex = /\s?contenteditable="false"/g;
            data.value = data.value.replaceAll(titleCleanupRegex, '');
            data.value = data.value.replaceAll(ctdCleanupRegex, '');
            data.value = data.value.replaceAll(editableCleanupRegex, '');
            handleEventAttributeChange();
          },
          change: (editorValue: string) => {
            onChangeLocal(editorValue);
            injectRelationTitles();
          },
          afterInit: () => {
            const updateValue = () => {
              if (editor.current && editor?.current?.value !== value) {
                editor.current.value = value;
                injectRelationTitles();
              }
            };

            if (editor.current) {
              editor.current.waitForReady().then(updateValue);
            }
          },
          afterSetMode: () => {
            if (!editor.current) return;
            const newMode = editor.current.getRealMode();
            if (newMode === Jodit.constants.MODE_WYSIWYG) {
              handleEventAttributeChange();
            } else if (newMode === Jodit.constants.MODE_SOURCE) {
              editor.current.setEditorValue(
                handleEventAttributeRestore(editor.current.value),
              );
            }

            injectRelationTitles();
          },
          changePlace: injectRelationTitles,
          blur: () => {
            onBlur(name);
          },
          focus: () => {
            onFocus?.(name);
          },
        },
        /**
         * Jodit derives external asset paths from the active script URL.
         * Under Vite HMR that URL becomes a transformed module path, which breaks
         * runtime config loading on reloads.
         */
        loadExternalConfig: false,
        /**
         * Jodit uses wrong path for autocomplete css plugin.
         * We are not using that plugin, so we can just disable it.
         */
        disablePlugins: ['autocomplete'],
        ...editorConfig,
      }) as JoditPro;
    }
  }, [
    buttons,
    controls,
    value,
    i18n.language,
    onBlur,
    onChangeLocal,
    injectRelationTitles,
    editorConfig,
    name,
    jwt,
    space,
    t,
    handleEventAttributeChange,
    handleEventAttributeRestore,
    editorReadonly,
    onMediaUpload,
    onFocus,
  ]);

  useEffect(() => {
    joditStateRef.current = 'active';
    return () => {
      joditStateRef.current = 'destruct';
    };
  }, []);

  useEffect(
    () => () => {
      if (!editor.current) return;

      if (editor.current.isFullSize) {
        editor.current.e.fire('toggleFullSize', false);

        // Clean up body overlow classes after full size
        document.body.classList.remove('overflow-hidden');
      }

      modal.close(`${modalId}-add-media`);
      modal.close(`${modalId}-add-relation`);

      editor.current.waitForReady().then(() => {
        if (joditStateRef.current === 'destruct' && editor.current) {
          editor.current.destruct();
          editor.current = null;
        }
      });
    },
    [modal, modalId],
  );

  useEffect(() => {
    const divElement = joditWrapperRef.current;
    if (!divElement) return;

    const observer = new ResizeObserver(() => {
      if (editor.current) {
        editor.current.events.fire('resize');
      }
    });

    observer.observe(divElement);
    return () => {
      observer.unobserve(divElement);
      observer?.disconnect();
    };
  }, []);

  return (
    <div
      className={twMerge(
        'w-full',
        'flex',
        'flex-col',
        'relative',
        additionalClasses,
      )}
    >
      <label
        className="text-sm text-slate-400 dark:text-slate-200 mb-1"
        {...getTestProps(testId, 'label')}
      >
        {label}
        {required && <RequiredTemplate />}
      </label>

      <FieldEditorsIndicator editors={editors} borderRadius={0}>
        <div
          data-field-name={name}
          ref={joditWrapperRef}
          className={twMerge(
            'jodit-wrapper',
            error != null && 'error',
            disabled && 'disabled',
            'dark:text-slate-900',
          )}
          {...getTestProps(testId, 'jodit-container')}
        >
          <textarea
            ref={textarea}
            id="editor"
            name="editor"
            /**
             * Without setting defaultValue and having loadExternalConfig set to false,
             * Jodit doesn't show the initial value.
             */
            defaultValue={value || ''}
          />
        </div>
      </FieldEditorsIndicator>

      <HelpErrorTextsTemplate helpText={helpText} error={error} />
    </div>
  );
};

export default RichText;
